import { accountStatus, } from "@gearbox-protocol/liquidator-v2-config";
import { AddressMap, AddressSet, getCuratorName, hexEq, TypedObjectUtils, } from "@gearbox-protocol/sdk/onchain";
/**
 * Groups results by account and computes each account's status.
 * Accounts matched by the whitelist are always `success`; pass an empty whitelist to skip it
 */
export function getAccountStatuses(results, whitelist) {
    const byAccount = new AddressMap();
    for (const r of results) {
        const accResults = byAccount.get(r.account) ?? [];
        accResults.push(r);
        byAccount.upsert(r.account, accResults);
    }
    const statuses = new AddressMap();
    for (const [account, accResults] of byAccount.entries()) {
        const creditManager = accResults[0].creditManager;
        const whitelisted = !!isAccountWhitelisted(whitelist, creditManager, account, accResults);
        statuses.upsert(account, {
            account: account.toLowerCase(),
            creditManager: creditManager.toLowerCase(),
            status: accountStatus(accResults, { whitelisted }),
        });
    }
    return statuses;
}
/**
 * Keeps results of credit managers that belong to the market configurator, if one is given
 */
export function filterResultsByMarketConfigurator(results, sdkState, marketConfigurator) {
    if (!marketConfigurator) {
        return results;
    }
    const creditManagers = new AddressSet(sdkState.markets
        .filter(m => hexEq(m.configurator, marketConfigurator))
        .flatMap(m => m.creditManagers)
        .map(cm => cm.creditManager.baseParams.addr));
    return results.filter(i => creditManagers.has(i.creditManager));
}
export function isAccountWhitelisted(whitelist, creditManager, account, tracks) {
    if (whitelist.has(account)) {
        return whitelist.get(account);
    }
    if (whitelist.has(creditManager)) {
        return whitelist.get(creditManager);
    }
    const assets = new AddressSet(tracks.flatMap(t => TypedObjectUtils.entries(t.balancesBefore)
        .filter(([_, v]) => BigInt(v) > 1n)
        .map(([a]) => a)));
    for (const a of assets.asArray()) {
        if (whitelist.has(a)) {
            return `Assets whitelisted: ${whitelist.get(a)}`;
        }
    }
    return undefined;
}
export function histogramLabel(n) {
    if (!n) {
        return "";
    }
    if (n < 1_000) {
        return n.toString(10);
    }
    if (n >= 1_000_000) {
        return `${Math.floor(n / 1_00_000) / 10}M`;
    }
    return `${Math.floor(n / 1_000)}K`;
}
/**
 * Returns pretty credit manager name and underlying
 * Returns undefined if credit manager is not found in SDK state
 * @param sdkState
 * @param creditManager
 */
export function describeCreditManager(sdkState, creditManager) {
    for (const market of sdkState.markets) {
        for (const suite of market.creditManagers) {
            if (hexEq(suite.creditManager.baseParams.addr, creditManager)) {
                const { name: name_, baseParams, underlying } = suite.creditManager;
                let name = `${name_} (v${baseParams.version})`;
                const curator = market
                    ? getCuratorName(market.configurator, sdkState.network)
                    : undefined;
                name = curator ? `${curator} ${name}` : name;
                return { name, underlying };
            }
        }
    }
    return undefined;
}
